import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { ObserverModel } from 'projects/core/src/lib/models/observer.model';
import { UserModel } from 'projects/core/src/lib/models/user.model';
import { ConfirmationService, SelectItem } from 'primeng/api';
import { AcModel } from 'projects/core/src/lib/models/ac.model';
import { Component, Input, OnInit, Output, EventEmitter, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import * as moment from 'moment';
import { CandidateModel } from 'projects/core/src/lib/models/candidate.model';
import { TranslocoService } from '@ngneat/transloco';
import { forkJoin } from 'rxjs';
import { AuthStateService } from 'projects/admin/src/app/services/auth-state.service';
import { AcPersonsCandidatesComponent } from './ac-persons-candidates/ac-persons-candidates.component';
import { AcPersonsObserversComponent } from './ac-persons-observers/ac-persons-observers.component';

@Component({
  selector: 'app-ac-persons',
  templateUrl: './ac-persons.component.html',
  styleUrls: ['./ac-persons.component.scss']
})
export class AcPersonsComponent implements OnInit {

  @Input() ac: AcModel = null;
  @Output() acChange = new EventEmitter<AcModel>();
  @Output() setEdit = new EventEmitter<string>();

  @ViewChild('candidates') candidates: AcPersonsCandidatesComponent;
  @ViewChild('participants') participants: AcPersonsObserversComponent;

  public inEdit = false;
  public loading = false;
  public submitting = false;
  public moderators: Array<UserModel> = [];
  public form: UntypedFormGroup = this.fb.group({
    moderator: new UntypedFormControl('', [Validators.required]),
  });
  public observers: Array<UserModel> = [];

  constructor(
    private fb: UntypedFormBuilder,
    private ui: UiService,
    private api: ApiService,
    private confirmation: ConfirmationService,
    private transloco: TranslocoService,
    private auth: AuthStateService
  ) { }

  ngOnInit(): void {
    this.loading = true;
    forkJoin([
      this.api.getModels('users', {type: 'moderator'}),
      this.api.getModels('users', {type: 'observer'}),
    ]).subscribe({
      next: (result: any) => {
        // moderators
        if (result[0] && result[0].data.length > 0) {
          result[0].data.forEach((moderator: any) => {
            this.moderators.push(new UserModel().deserialize(moderator));
          });
        }
        this.moderators.sort((a: UserModel, b: UserModel) => {
          return (a.lastname > b.lastname) ? 1 : ((b.lastname > a.lastname) ? -1 : 0);
        });
        // observers
        if (result[1] && result[1].data.length > 0) {
          result[1].data.forEach((observer: any) => {
            this.observers.push(new UserModel().deserialize(observer));
          });
        }
      }
    }).add(() => {
      this.loading = false;
    });
    this.ui.setFocus();
  }

  onEdit(): void {
    if (this.auth.hasRight('admin.ac.edit')) {
      this.startEdit();
    }
  }

  onCancel(): void {
    this.stopEdit();
  }

  onSendInvitations(candidate: any = null, observer: any = null): void {
    if (this.auth.hasRight('admin.ac.edit')) {
      const payload: any = {};
      let message = this.transloco.translate('ac_persons.sending_all_invitations');
      let successText = this.transloco.translate('ac_persons.candidate_and_observer');
      if (candidate !== null) {
        successText = candidate.name;
        message = this.transloco.translate('ac_persons.sending_for_candidate', { value: successText });
        payload.type = 'candidate';
        payload.recipient = candidate.id;
      } else if (observer !== null) {
        successText = observer.name;
        message = this.transloco.translate('ac_persons.sending_for_observer', { value: successText });
        payload.type = 'observer';
        payload.recipient = observer.id;
      }
      this.confirmation.confirm({
        message,
        header: this.transloco.translate('ac_persons.invitations'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.api.sendAcInvitations(this.ac.id, payload).subscribe({
            next: (result: any) => {
              const time = moment();
              if (candidate !== null) {
                candidate.invited_at = time;
              } else if (observer !== null) {
                observer.invited_at = time;
              } else {
                this.ac.candidates.forEach((actual: CandidateModel) => {
                  actual.invited_at = time;
                });
                this.ac.observers.forEach((actual: ObserverModel) => {
                  actual.invited_at = time;
                });
              }
              this.acChange.emit(new AcModel().deserialize(result.data));
              this.ui.toastSuccess(this.transloco.translate('ac_persons.invitations_sended_to', { value: successText }), this.transloco.translate('ac_persons.invitations'));
            },
            error: (error: any) => {
              this.ui.toastError(this.transloco.translate(error.message), this.transloco.translate('ac_persons.invitations'));
            }
          });
        }
      });
    }
  }

  onSubmit(): void {
    this.ui.hideMessage();
    if (this.form.valid === true) {
      // this.submitting = true;
      const payload = {
        moderator: this.form.value.moderator.id,
        candidates: this.candidates.getPayload(),
        observers: this.participants.getPayload()
      };
      this.api.saveAcPersons(this.ac.id, payload).subscribe({
        next: (result: any) => {
          this.ui.toastSuccess(this.transloco.translate('ac_persons.persons_saved'), this.transloco.translate('common.saved'));
          this.acChange.emit(new AcModel().deserialize(result.data));
          this.stopEdit();
        },
        error: (error: any) => {
          this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
          FormHelper.setBackendErrors(this.form, error.fielderrors);
        }
      }).add(() => {
        this.submitting = false;
    });
    } else {
      this.ui.showMessage('error', this.transloco.translate('common.error_on_form_please_correct'), this.transloco.translate('common.error'));
      FormHelper.markFormAsDirty(this.form);
    }
  }

  startEdit(): void {
    if (this.ac?.moderator !== null) {
      this.form.patchValue({
        moderator: this.moderators.find(mod => mod.id === this.ac.moderator.id)
      });
    }
    this.inEdit = true;
    this.setEdit.emit('persons');
    this.ui.setFormFocus();
  }

  stopEdit(): void {
    this.inEdit = false;
    this.setEdit.emit(null);
    this.ui.setFocus();
  }

  canShowInvitation(): boolean {
    return (
      !this.ac.isCancelled() &&
      this.ac.status !== 'finished' &&
      this.ac.candidates.length > 0
    );
  }

  invitationSended(): boolean {
    return this.ac?.invitationSended();
  }

}
